<template>
    <v-dialog v-model="dialog" max-width="680" scrollable :persistent="saving" :aria-labelledby="`${formId}-title`">
        <template v-slot:activator="{ on, attrs }">
            <v-btn
                v-if="tableView"
                class="branch-trigger"
                color="#f0784c"
                elevation="0"
                outlined
                small
                :class="{ 'branch-trigger--icon': iconOnly }"
                :icon="iconOnly"
                :aria-label="iconOnly ? 'Create branch' : undefined"
                :title="iconOnly ? 'Create branch' : undefined"
                v-bind="attrs"
                v-on="on"
            >
                <v-icon v-if="iconOnly">{{ mdiPlus }}</v-icon>
                <template v-else>Create</template>
            </v-btn>
            <v-btn
                v-if="!tableView"
                v-bind="attrs"
                class="branch-trigger branch-trigger--tree"
                color="#db5120"
                elevation="0"
                v-on="on"
            >
                Add Branch
            </v-btn>
        </template>
        <v-card class="branch-dialog" dark elevation="0" :aria-busy="saving ? 'true' : 'false'">
            <v-card-title class="branch-dialog__header">
                <p class="eyebrow">Organization workspace</p>
                <h2 :id="`${formId}-title`">{{ actionLabel }}</h2>
                <p>Give your {{ entityLabel }} a name and confirm its address before creating it.</p>
            </v-card-title>
            <v-card-text class="branch-dialog__body">
                <section v-if="!noParentOrg" class="parent-context" aria-label="Parent organization">
                    <span>New branch under</span>
                    <strong>{{ parentName }}</strong>
                </section>

                <!-- OrgAddress owns a v-form. Keep the name form separate to avoid nested forms. -->
                <form :id="formId" class="name-section" novalidate @submit.prevent="createSub">
                    <label :for="`${formId}-name`">{{ noParentOrg ? 'Organization name' : 'Branch name' }}</label>
                    <p :id="`${formId}-name-help`" class="section-help">Use a name your team will recognize.</p>
                    <input
                        :id="`${formId}-name`"
                        v-model="orgName"
                        name="organizationName"
                        type="text"
                        autocomplete="organization"
                        required
                        :disabled="saving"
                        :aria-invalid="nameError ? 'true' : 'false'"
                        :aria-describedby="`${formId}-name-help${nameError ? ` ${formId}-name-error` : ''}`"
                        @blur="nameTouched = true"
                    />
                    <p v-if="nameError" :id="`${formId}-name-error`" class="field-error" role="alert">{{
                        nameError
                    }}</p>
                </form>

                <fieldset class="address-section" :disabled="saving">
                    <legend>Location details</legend>
                    <p class="section-help">
                        {{
                            noParentOrg
                                ? 'Enter the organization address.'
                                : 'Starts with the parent address. Update it for this branch if needed.'
                        }}
                    </p>
                    <org-address
                        v-if="dialog"
                        :key="formVersion"
                        :current-level="currentLevel"
                        :org-name="trimmedName"
                        view="create"
                        @address="address = $event"
                        @valid="addressValid = $event"
                        @submit.native.prevent.stop
                        @keydown.enter.native="addressEnter"
                    />
                </fieldset>

                <section
                    class="branch-summary"
                    aria-label="Review new organization"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    <h3>Ready to create</h3>
                    <dl>
                        <div
                            ><dt>{{ noParentOrg ? 'Organization' : 'Branch' }}</dt
                            ><dd>{{ trimmedName || 'Add a name above' }}</dd></div
                        >
                        <div v-if="!noParentOrg"
                            ><dt>Parent</dt><dd>{{ parentName }}</dd></div
                        >
                        <div
                            ><dt>Address</dt
                            ><dd>
                                <template v-if="hasAddressPreview">
                                    <print-org-address :address="address" />
                                    <span>{{ address.country }}</span>
                                </template>
                                <span v-else>Add an address above</span>
                            </dd></div
                        >
                    </dl>
                </section>
                <p v-if="!noParentOrg && !parentId" class="form-error" role="alert">
                    No parent organization is selected. Select an organization before creating a branch.
                </p>
                <p v-if="saveError" class="form-error" role="alert">{{ saveError }}</p>
            </v-card-text>
            <v-card-actions class="branch-dialog__actions">
                <v-btn class="branch-secondary cancel-branch" type="button" text :disabled="saving" @click="cancel"
                    >Cancel</v-btn
                >
                <v-btn
                    class="branch-primary create-branch"
                    type="submit"
                    :form="formId"
                    color="#db5120"
                    elevation="0"
                    :loading="saving"
                    :disabled="!canSubmit"
                    >{{ actionLabel }}</v-btn
                >
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import gql from 'graphql-tag'
import OrgAddress from './address.vue'
import PrintOrgAddress from './print_address.vue'
import { mdiPlus } from '@mdi/js'

export default {
    name: 'CreateSubOrg',
    components: { OrgAddress, PrintOrgAddress },
    props: {
        currentLevel: {
            type: Object,
            required: true
        },
        tableView: {
            type: Boolean,
            required: true
        },
        noParentOrg: {
            type: Boolean,
            required: true
        },
        iconOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            mdiPlus,
            dialog: false,
            orgName: '',
            address: null,
            addressValid: false,
            nameTouched: false,
            saving: false,
            saveError: '',
            formVersion: 0
        }
    },
    computed: {
        formId() {
            return `create-organization-${this._uid}`
        },
        entityLabel() {
            return this.noParentOrg ? 'organization' : 'branch'
        },
        actionLabel() {
            return this.noParentOrg ? 'Create organization' : 'Create branch'
        },
        parentId() {
            return this.currentLevel.baseAccountId
        },
        parentName() {
            return this.currentLevel.baseAccountName || this.currentLevel.organizationName || 'No organization selected'
        },
        contextKey() {
            return JSON.stringify([this.noParentOrg, this.currentLevel])
        },
        trimmedName() {
            return this.orgName.trim()
        },
        nameError() {
            if (!this.nameTouched || this.trimmedName) return ''
            return this.noParentOrg ? 'Enter an organization name.' : 'Enter a branch name.'
        },
        hasAddressPreview() {
            return (
                this.address && Object.values(this.address).some((value) => typeof value === 'string' && value.trim())
            )
        },
        canSubmit() {
            return (
                this.dialog &&
                !!this.trimmedName &&
                this.addressValid === true &&
                !!this.address &&
                (this.noParentOrg || !!this.parentId) &&
                !this.saving
            )
        }
    },
    watch: {
        dialog() {
            this.resetForm()
        },
        currentLevel: {
            deep: true,
            handler() {
                this.resetForm()
            }
        },
        noParentOrg() {
            this.resetForm()
        }
    },
    beforeDestroy() {
        this.formVersion += 1
    },
    methods: {
        resetForm() {
            this.formVersion += 1
            this.orgName = ''
            this.address = null
            this.addressValid = false
            this.nameTouched = false
            this.saveError = ''
        },
        cancel() {
            if (!this.saving) this.dialog = false
        },
        addressEnter(event) {
            // Autocompletes use Enter to select a country/state, never to create a branch.
            if (
                event.isComposing ||
                event.defaultPrevented ||
                event.target.tagName !== 'INPUT' ||
                event.target.closest('.v-select, [role="combobox"]')
            )
                return
            event.preventDefault()
            this.createSub()
        },
        failureMessage(detail) {
            const reason = typeof detail === 'string' && detail.trim() ? ` ${detail}` : ''
            return `Could not create the ${this.entityLabel}.${reason} Please try again.`
        },
        async createSub() {
            if (!this.dialog || this.saving) return
            this.nameTouched = true
            if (!this.canSubmit) return
            this.saving = true
            this.saveError = ''
            const version = this.formVersion
            const context = this.contextKey
            const name = this.trimmedName
            const isCurrent = () => this.dialog && version === this.formVersion && context === this.contextKey
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation CreateAccountOrganization($input: AccountOrganizationInput!) {
                            createAccountOrganization(input: $input) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: {
                        input: {
                            organizationName: name,
                            // Keep the existing API contract, including undefined/null for a parent-less context.
                            parentOrgId: this.parentId,
                            address: { ...this.address }
                        }
                    }
                })
                if (!isCurrent()) return
                const result = response && response.data && response.data.createAccountOrganization
                if (result && result.success === true) {
                    this.dialog = false
                    this.$emit('created', `'${name}' successfully created`)
                } else {
                    this.saveError = this.failureMessage(result && result.error)
                }
            } catch (error) {
                if (isCurrent()) this.saveError = this.failureMessage(error && error.message)
            } finally {
                this.saving = false
            }
        }
    }
}
</script>

<style scoped>
.branch-trigger,
.branch-dialog {
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 12px;
}
.branch-trigger {
    border-radius: 8px;
    letter-spacing: 0;
    text-transform: none;
}
.branch-trigger--tree {
    display: block;
    margin: auto;
    min-width: 150px;
    color: #fff;
}
.branch-trigger--icon {
    width: 36px;
    min-width: 36px !important;
    height: 36px;
    padding: 0 !important;
    border-radius: 9px;
}
.branch-trigger--icon ::v-deep .v-icon {
    color: #ffb396;
}
.branch-dialog.v-card {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 48px);
    border: 1px solid #454953;
    border-radius: 12px;
    background: #282b33;
    color: #f5f5f7;
}
.branch-dialog__header {
    flex: 0 0 auto;
    display: block;
    padding: 20px 24px;
    border-bottom: 1px solid #454953;
}
.branch-dialog h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: -0.02em;
    overflow-wrap: anywhere;
}
.branch-dialog .branch-dialog__body {
    min-height: 0;
    padding: 20px 24px;
    overflow-y: auto;
    color: #d4d6dd;
    font-size: 12px;
    line-height: 1.6;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.branch-dialog__body::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}
.branch-dialog__body::-webkit-scrollbar-track {
    background: #20222a;
    border-radius: 9px;
}
.branch-dialog__body::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
.branch-dialog__body::-webkit-scrollbar-thumb:hover {
    background: #f0784c;
}
.branch-dialog__header p {
    margin: 8px 0 0;
    color: #bfc3ce;
    font-size: 12px;
    line-height: 1.5;
}
.branch-dialog__header .eyebrow {
    margin: 0 0 8px;
    color: #f0784c;
    font-weight: 600;
}
.parent-context {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    margin-bottom: 20px;
    padding: 10px 14px;
    border: 1px solid #454953;
    border-radius: 8px;
    background: #22252d;
    overflow-wrap: anywhere;
}
.parent-context span {
    color: #f0784c;
}
.parent-context strong {
    min-width: 0;
    color: #f5f5f7;
}
.name-section {
    margin-bottom: 20px;
}
.name-section label,
.address-section legend,
.branch-summary h3 {
    margin: 0 0 4px;
    color: #f5f5f7;
    font-size: 14px;
    font-weight: 600;
}
.branch-dialog .section-help {
    margin: 0 0 12px;
    color: #bfc3ce;
}
.name-section input {
    width: 100%;
    min-height: 40px;
    padding: 10px 12px;
    border: 1px solid #686d79;
    border-radius: 8px;
    background: #22252d;
    color: #f5f5f7;
    font: inherit;
}
.name-section input[aria-invalid='true'] {
    border-color: #ffb4ac;
}
.branch-dialog .field-error {
    margin: 6px 0 0;
    color: #ffb4ac;
}
.address-section {
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
}
.address-section:disabled,
.name-section input:disabled {
    opacity: 0.65;
}
.branch-summary {
    margin-top: 16px;
    padding: 12px 14px;
    border-left: 3px solid #f0784c;
    border-radius: 0 8px 8px 0;
    background: #22252d;
    overflow-wrap: anywhere;
}
.branch-summary dl {
    margin: 8px 0 0;
}
.branch-summary dl > div {
    display: grid;
    grid-template-columns: 90px minmax(0, 1fr);
    gap: 12px;
    margin-top: 4px;
}
.branch-summary dt {
    color: #bfc3ce;
}
.branch-summary dd {
    margin: 0;
}
.branch-dialog .form-error {
    margin: 12px 0 0;
    padding: 12px;
    border: 1px solid #a45c58;
    border-radius: 8px;
    color: #ffb4ac;
    overflow-wrap: anywhere;
}
.branch-dialog__actions {
    flex: 0 0 auto;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 24px;
    border-top: 1px solid #454953;
    background: #22252d;
}
.branch-dialog .v-btn {
    max-width: 100%;
    height: auto;
    min-height: 36px;
    margin: 0;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 12px;
    letter-spacing: 0;
    text-transform: none;
}
.branch-dialog .branch-secondary {
    border-color: #686d79;
    color: #e0e2e8;
}
.branch-dialog .branch-primary {
    color: #fff;
}
.branch-dialog ::v-deep .v-btn__content {
    white-space: normal;
    overflow-wrap: anywhere;
}
.branch-dialog ::v-deep .v-input,
.branch-dialog ::v-deep .v-label,
.branch-dialog ::v-deep input {
    font-family: inherit;
    font-size: 12px;
}
.branch-dialog ::v-deep .v-text-field--outlined .v-input__slot {
    background: #22252d;
    border-radius: 8px;
}
.branch-dialog ::v-deep .v-text-field--outlined:not(.v-input--is-focused):not(.error--text) fieldset {
    border-color: #686d79;
}
.branch-trigger:focus-visible,
.name-section input:focus-visible,
.branch-dialog ::v-deep .v-btn:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
@media (max-width: 600px) {
    .branch-dialog__header,
    .branch-dialog .branch-dialog__body,
    .branch-dialog__actions {
        padding: 16px;
    }
    .branch-summary dl > div {
        grid-template-columns: minmax(0, 1fr);
        gap: 0;
    }
}
</style>
